import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Icon from '../../components/AppIcon';
import { tiendaDb } from '../../lib/supabase';
import EpelsaBascula from './components/EpelsaBascula';
import EpelsaConfiguracion from './components/EpelsaConfiguracion';

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const ACCENT = '#34D399';
const GOLD = '#D6EF4E';
const BLUE = '#60A5FA';
const PURPLE = '#A78BFA';
const RED = '#F87171';
const ORANGE = '#FB923C';
const BATCH_SIZE = 200;

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const fmt = (n, d = 2) => typeof n === 'number' ? n?.toLocaleString('es-ES', { minimumFractionDigits: d, maximumFractionDigits: d }) : '—';
const fmtEur = (n) => typeof n === 'number' ? `${fmt(n)} €` : '—';
const fmtDate = (ts) => ts ? new Date(ts)?.toLocaleDateString('es-ES', { timeZone: 'Europe/Madrid', day: '2-digit', month: '2-digit', year: '2-digit' }) : '—';
const fmtTime = (ts) => ts ? new Date(ts)?.toLocaleTimeString('es-ES', { timeZone: 'Europe/Madrid', hour: '2-digit', minute: '2-digit' }) : '—';
const todayMadrid = () => new Date()?.toLocaleDateString('sv-SE', { timeZone: 'Europe/Madrid' });

const normalizePago = (metodo_codigo, metodo_nombre) => {
  const code = (metodo_codigo || '')?.toLowerCase();
  const name = (metodo_nombre || '')?.toLowerCase();
  if (code === 't.credito' || code === 'credito' || name?.includes('tarjeta') || name?.includes('credito') || name?.includes('crédito')) return 'Tarjeta';
  if (code === 'efectivo' || name?.includes('efectivo') || name?.includes('cash')) return 'Efectivo';
  if (code === 'bizum' || name?.includes('bizum')) return 'Bizum';
  return metodo_nombre || metodo_codigo || 'Otros';
};

// ─── PRESET RANGES ────────────────────────────────────────────────────────────
const shiftDate = (dateStr, days) => {
  const [y, m, d] = String(dateStr).split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
};

const getPresetRange = (preset) => {
  const todayStr = todayMadrid();
  const [year, month, day] = todayStr.split('-').map(Number);
  const today = new Date(Date.UTC(year, month - 1, day));
  const dow = today.getUTCDay();
  const daysFromMonday = dow === 0 ? 6 : dow - 1;
  const weekStart = shiftDate(todayStr, -daysFromMonday);
  const prevWeekEnd = shiftDate(weekStart, -1);
  const prevWeekStart = shiftDate(prevWeekEnd, -6);
  const monthStart = `${year}-${String(month).padStart(2, '0')}-01`;
  const prevMonth = new Date(Date.UTC(year, month - 2, 1));
  const prevMonthStart = prevMonth.toISOString().slice(0, 10);
  const prevMonthEnd = shiftDate(monthStart, -1);
  const quarterStart = new Date(Date.UTC(year, Math.floor((month - 1) / 3) * 3, 1)).toISOString().slice(0, 10);
  switch (preset) {
    case 'hoy': return { desde: todayStr, hasta: todayStr };
    case 'ayer': { const d = shiftDate(todayStr, -1); return { desde: d, hasta: d }; }
    case 'ultimos_7': return { desde: shiftDate(todayStr, -6), hasta: todayStr };
    case 'ultimos_30': return { desde: shiftDate(todayStr, -29), hasta: todayStr };
    case 'semana': return { desde: weekStart, hasta: todayStr };
    case 'semana_anterior': return { desde: prevWeekStart, hasta: prevWeekEnd };
    case 'mes': return { desde: monthStart, hasta: todayStr };
    case 'mes_anterior': return { desde: prevMonthStart, hasta: prevMonthEnd };
    case 'trimestre': return { desde: quarterStart, hasta: todayStr };
    case 'anio': return { desde: `${year}-01-01`, hasta: todayStr };
    case 'mayo_2026': return { desde: '2026-05-01', hasta: '2026-05-31' };
    case 'junio_2026': return { desde: '2026-06-01', hasta: '2026-06-30' };
    case 'julio_2026': return { desde: '2026-07-01', hasta: '2026-07-31' };
    case 'agosto_2026': return { desde: '2026-08-01', hasta: '2026-08-31' };
    case 'historico': return { desde: '2026-05-01', hasta: todayStr };
    default: return { desde: todayStr, hasta: todayStr };
  }
};

// ─── EPELSA VENTAS SERVICE ────────────────────────────────────────────────────
const epelsaVentasService = {
  // Get tickets with server-side filters and pagination
  getTickets: async ({ desde, hasta, search, vendedor, formaPago, tipo, importeMin, importeMax, pageSize, page, sortBy, sortDir }) => {
    let query = tiendaDb?.from('epelsa_tickets')
      ?.select('id, numero_ticket, fecha, fecha_operacion, total, base_imponible, iva_importe, vendedor_nombre, vendedor_codigo, lineas_numero, forma_pago, estado, numero_factura, cliente_nombre, cliente_codigo, balanza_codigo, scale_codigo, counter_codigo, counter_nombre, factura_simplificada', { count: 'exact' });

    if (desde) query = query?.gte('fecha_operacion', desde);
    if (hasta) query = query?.lte('fecha_operacion', hasta);
    if (importeMin !== '' && importeMin != null) query = query?.gte('total', parseFloat(importeMin));
    if (importeMax !== '' && importeMax != null) query = query?.lte('total', parseFloat(importeMax));
    if (tipo === 'ventas') query = query?.gte('total', 0);
    if (tipo === 'devoluciones') query = query?.lt('total', 0);
    if (vendedor) query = query?.or(`vendedor_nombre.ilike.%${vendedor}%,vendedor_codigo.ilike.%${vendedor}%`);

    // Filtrado real por forma de pago. Antes este filtro se ignoraba en
    // getTickets y solo se utilizaba en una función auxiliar.
    if (formaPago) {
      const { data: pagos, error: pagosError } = await tiendaDb
        ?.from('epelsa_ticket_pagos')
        ?.select('ticket_id, metodo_codigo, metodo_nombre');

      if (pagosError) throw pagosError;

      const idsPago = (pagos || [])
        ?.filter(p => normalizePago(p?.metodo_codigo, p?.metodo_nombre) === formaPago)
        ?.map(p => p?.ticket_id)
        ?.filter(Boolean);

      if (!idsPago?.length) return { tickets: [], total: 0 };
      query = query?.in('id', idsPago);
    }

    if (search) {
      const s = search?.trim();
      if (/^\d+$/?.test(s)) {
        query = query?.or(`numero_ticket.ilike.%${s}%,numero_factura.ilike.%${s}%`);
      } else {
        query = query?.or(`numero_ticket.ilike.%${s}%,numero_factura.ilike.%${s}%,vendedor_nombre.ilike.%${s}%,cliente_nombre.ilike.%${s}%`);
      }
    }

    const sortCol = sortBy || 'fecha';
    query = query?.order(sortCol, { ascending: sortDir === 'asc' });
    const from = (page - 1) * pageSize;
    query = query?.range(from, from + pageSize - 1);

    const { data, error, count } = await query;
    if (error) throw error;

    // If filtering by forma_pago, we need to filter via pagos table
    // For now, return tickets and let KPIs handle pago filtering
    return { tickets: data || [], total: count || 0 };
  },

  // Get tickets filtered by payment method (requires pagos join)
  getTicketsByPago: async ({ desde, hasta, formaPago, pageSize, page }) => {
    // Get ticket IDs that have the specified payment method
    let pagoQuery = tiendaDb?.from('epelsa_ticket_pagos')?.select('ticket_id, metodo_codigo, metodo_nombre');
    const { data: pagos, error: pErr } = await pagoQuery;
    if (pErr) throw pErr;

    const matchingIds = new Set();
    for (const p of pagos || []) {
      const label = normalizePago(p?.metodo_codigo, p?.metodo_nombre);
      if (label === formaPago) matchingIds?.add(p?.ticket_id);
    }
    return Array.from(matchingIds);
  },

  // Get KPIs for a date range — uses server-side RPC to bypass 1000-row limit.
  // tienda.get_epelsa_kpis performs SUM/COUNT over the FULL dataset in PostgreSQL.
  getKPIs: async (desde, hasta) => {
    if (!desde || !hasta) return { ventas: 0, nTickets: 0, ticketMedio: 0, efectivo: 0, tarjeta: 0, bizum: 0, otros: 0, diasConVentas: 0 };
    const { data, error } = await tiendaDb?.rpc('get_epelsa_kpis', {
      p_desde: desde,
      p_hasta: hasta,
    });
    if (error) throw error;
    const row = data?.[0] || {};
    const ventas = parseFloat(row?.total_ventas) || 0;
    const nTickets = parseInt(row?.n_tickets) || 0;
    const ticketMedio = nTickets > 0 ? ventas / nTickets : 0;
    return {
      ventas,
      nTickets,
      ticketMedio,
      efectivo: parseFloat(row?.efectivo) || 0,
      tarjeta: parseFloat(row?.tarjeta) || 0,
      bizum: parseFloat(row?.bizum) || 0,
      otros: parseFloat(row?.otros) || 0,
      diasConVentas: parseInt(row?.dias_con_ventas) || 0,
    };
  },

  getAlerts: async (desde, hasta) => {
    if (!desde || !hasta) return null;
    const { data, error } = await tiendaDb?.rpc('get_epelsa_sales_alerts', {
      p_desde: desde,
      p_hasta: hasta,
    });
    if (error) throw error;
    const row = data?.[0] || {};
    return {
      ticketsSinPago: parseInt(row?.tickets_sin_pago) || 0,
      devoluciones: parseInt(row?.devoluciones) || 0,
      importeDevoluciones: parseFloat(row?.importe_devoluciones) || 0,
      ticketMaximo: parseFloat(row?.ticket_maximo) || 0,
      ultimoTicket: row?.ultimo_ticket || null,
      diferenciaPagos: parseFloat(row?.diferencia_pagos) || 0,
      ticketsSinCliente: parseInt(row?.tickets_sin_cliente) || 0,
      lineasSinFamilia: parseInt(row?.lineas_sin_familia) || 0,
      ventasPeriodo: parseFloat(row?.ventas_periodo) || 0,
      ventasPeriodoAnterior: parseFloat(row?.ventas_periodo_anterior) || 0,
      variacionPct: row?.variacion_pct == null ? null : parseFloat(row?.variacion_pct),
    };
  },

  // Get artículos (líneas) with server-side filters and pagination
  getLineas: async ({ desde, hasta, search, vendedor, familia, pageSize, page, sortBy, sortDir }) => {
    // First get ticket IDs in date range
    let ticketQuery = tiendaDb?.from('epelsa_tickets')?.select('id, fecha, fecha_operacion, vendedor_nombre, vendedor_codigo');
    if (desde) ticketQuery = ticketQuery?.gte('fecha_operacion', desde);
    if (hasta) ticketQuery = ticketQuery?.lte('fecha_operacion', hasta);
    if (vendedor) ticketQuery = ticketQuery?.or(`vendedor_nombre.ilike.%${vendedor}%,vendedor_codigo.ilike.%${vendedor}%`);

    const { data: tickets, error: tErr } = await ticketQuery;
    if (tErr) throw tErr;
    if (!tickets?.length) return { lineas: [], total: 0 };

    const ticketMap = {};
    for (const t of tickets) ticketMap[t.id] = t;
    const ticketIds = tickets?.map((t) => t?.id);

    // Fetch lineas in batches with filters
    const allLineas = [];
    for (let i = 0; i < ticketIds?.length; i += BATCH_SIZE) {
      const batch = ticketIds?.slice(i, i + BATCH_SIZE);
      let lQuery = tiendaDb?.from('epelsa_ticket_lineas')
        ?.select('id, ticket_id, articulo_codigo, descripcion, familia_codigo, cantidad, peso, precio_unitario, iva_porcentaje, total, sale_form, fecha_linea');
      lQuery = lQuery?.in('ticket_id', batch);
      if (familia) lQuery = lQuery?.eq('familia_codigo', familia);
      if (search) lQuery = lQuery?.or(`descripcion.ilike.%${search}%,articulo_codigo.ilike.%${search}%`);
      const { data: l } = await lQuery;
      if (l) allLineas?.push(...l);
    }

    // Attach ticket info
    const enriched = allLineas?.map((l) => ({
      ...l,
      ticket: ticketMap?.[l?.ticket_id] || null,
    }));

    // Sort
    const sortCol = sortBy || 'fecha_linea';
    enriched?.sort((a, b) => {
      let va = a?.[sortCol] || a?.ticket?.fecha || '';
      let vb = b?.[sortCol] || b?.ticket?.fecha || '';
      return sortDir === 'asc' ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
    });

    const total = enriched?.length;
    const from = (page - 1) * pageSize;
    return { lineas: enriched?.slice(from, from + pageSize), total };
  },

  // Get ticket detail with pagos and lineas
  getTicketDetail: async (ticketId) => {
    const [ticketResult, pagosResult, lineasResult] = await Promise.all([
      tiendaDb?.from('epelsa_tickets')?.select('*')?.eq('id', ticketId)?.single(),
      tiendaDb?.from('epelsa_ticket_pagos')?.select('*')?.eq('ticket_id', ticketId),
      tiendaDb?.from('epelsa_ticket_lineas')?.select('*')?.eq('ticket_id', ticketId),
    ]);
    const ticketData = ticketResult?.data;
    const pagosData = pagosResult?.data;
    const lineasData = lineasResult?.data;
    return { ticket: ticketData, pagos: pagosData || [], lineas: lineasData || [] };
  },

  // Get distinct vendedores
  getVendedores: async () => {
    const { data } = await tiendaDb?.from('epelsa_tickets')?.select('vendedor_nombre, vendedor_codigo')?.not('vendedor_nombre', 'is', null)?.limit(200);
    const seen = new Set();
    return (data || [])?.filter((v) => { const k = v?.vendedor_nombre || v?.vendedor_codigo; if (seen?.has(k)) return false; seen?.add(k); return true; });
  },

  // Get distinct familias
  getFamilias: async () => {
    const { data } = await tiendaDb?.from('epelsa_ticket_lineas')?.select('familia_codigo')?.not('familia_codigo', 'is', null)?.limit(500);
    const seen = new Set();
    return (data || [])?.map((f) => f?.familia_codigo)?.filter((f) => { if (seen?.has(f)) return false; seen?.add(f); return true; })?.sort();
  },
};

// ─── TICKET DETAIL MODAL ──────────────────────────────────────────────────────
const TicketDetailModal = ({ ticketId, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    epelsaVentasService?.getTicketDetail(ticketId)?.then(setDetail)?.catch(() => setDetail(null))?.finally(() => setLoading(false));
  }, [ticketId]);

  const t = detail?.ticket;
  const isDevolucion = t && parseFloat(t?.total) < 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }}>
      <div className="w-full max-w-xl rounded-2xl max-h-[92vh] overflow-y-auto" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.10)' }}>
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4" style={{ background: '#141414', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <div>
            <h3 className="font-oranienbaum font-bold text-white text-lg">Ticket #{t?.numero_ticket || '...'}</h3>
            <p className="font-oranienbaum text-white/40 text-xs">{t ? `${fmtDate(t?.fecha)} · ${fmtTime(t?.fecha)}` : '...'}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-white/40 hover:text-white/80 transition-colors"><Icon name="X" size={16} /></button>
        </div>
        <div className="px-6 py-4 space-y-4">
          {loading ? (
            <div className="flex justify-center py-8"><div className="w-8 h-8 rounded-full border-2 border-white/10 border-t-[#34D399] animate-spin" /></div>
          ) : !detail ? (
            <p className="font-oranienbaum text-white/40 text-sm text-center py-8">Error al cargar el ticket</p>
          ) : (
            <>
              {/* Info */}
              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: 'Vendedor', value: t?.vendedor_nombre || t?.vendedor_codigo || '—' },
                  { label: 'Caja/Balanza', value: t?.counter_nombre || t?.balanza_codigo || t?.scale_codigo || '—' },
                  { label: 'Cliente', value: t?.cliente_nombre || '—' },
                  { label: 'Factura', value: t?.numero_factura || '—' },
                  { label: 'Base imponible', value: fmtEur(parseFloat(t?.base_imponible)) },
                  { label: 'IVA', value: fmtEur(parseFloat(t?.iva_importe)) },
                  { label: 'Total', value: fmtEur(parseFloat(t?.total)), color: isDevolucion ? ORANGE : ACCENT },
                  { label: 'Tipo', value: isDevolucion ? 'Devolución' : 'Venta', color: isDevolucion ? ORANGE : ACCENT },
                ]?.map((item, i) => (
                  <div key={i} className="p-2 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <p className="font-oranienbaum text-white/40 text-xs">{item?.label}</p>
                    <p className="font-oranienbaum font-bold text-sm" style={{ color: item?.color || 'rgba(255,255,255,0.85)' }}>{item?.value}</p>
                  </div>
                ))}
              </div>
              {/* Pagos */}
              {detail?.pagos?.length > 0 && (
                <div>
                  <p className="font-oranienbaum text-white/40 text-xs uppercase tracking-widest mb-2">Formas de pago</p>
                  <div className="space-y-1">
                    {detail?.pagos?.map((p, i) => (
                      <div key={i} className="flex justify-between px-3 py-2 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                        <span className="font-oranienbaum text-white/60 text-sm">{normalizePago(p?.metodo_codigo, p?.metodo_nombre)}</span>
                        <span className="font-oranienbaum font-bold text-sm" style={{ color: ACCENT }}>{fmtEur(parseFloat(p?.importe_entregado))}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Líneas */}
              {detail?.lineas?.length > 0 && (
                <div>
                  <p className="font-oranienbaum text-white/40 text-xs uppercase tracking-widest mb-2">Líneas ({detail?.lineas?.length})</p>
                  <div className="space-y-1">
                    {detail?.lineas?.map((l, i) => (
                      <div key={i} className="px-3 py-2 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                        <div className="flex items-center justify-between">
                          <span className="font-oranienbaum text-white/80 text-sm flex-1 truncate">{l?.descripcion || l?.articulo_codigo || '—'}</span>
                          <span className="font-oranienbaum font-bold text-sm ml-2" style={{ color: GOLD }}>{fmtEur(parseFloat(l?.total))}</span>
                        </div>
                        <div className="flex items-center gap-3 mt-0.5">
                          {l?.articulo_codigo && <span className="font-oranienbaum text-white/30 text-xs">Cód: {l?.articulo_codigo}</span>}
                          {l?.familia_codigo && <span className="font-oranienbaum text-white/30 text-xs">Fam: {l?.familia_codigo}</span>}
                          {l?.peso > 0 && <span className="font-oranienbaum text-white/40 text-xs">{fmt(l?.peso, 3)} kg</span>}
                          {l?.cantidad > 0 && <span className="font-oranienbaum text-white/40 text-xs">×{fmt(l?.cantidad, 0)}</span>}
                          {l?.precio_unitario > 0 && <span className="font-oranienbaum text-white/40 text-xs">{fmtEur(parseFloat(l?.precio_unitario))}/u</span>}
                          {l?.iva_porcentaje > 0 && <span className="font-oranienbaum text-white/30 text-xs">IVA {l?.iva_porcentaje}%</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── TABS ─────────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'ventas', label: 'Ventas', icon: 'TrendingUp' },
  { id: 'bascula', label: 'Báscula Epelsa', icon: 'Scale' },
  { id: 'config-epelsa', label: 'Configuración Epelsa', icon: 'Settings' },
];

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const TiendaVentas = () => {
  const [activeTab, setActiveTab] = useState('ventas');
  const [vistaActiva, setVistaActiva] = useState('tickets'); // 'tickets' | 'articulos'

  // Filters
  const [preset, setPreset] = useState('hoy');
  const [desde, setDesde] = useState(() => getPresetRange('hoy')?.desde);
  const [hasta, setHasta] = useState(() => getPresetRange('hoy')?.hasta);
  const [search, setSearch] = useState('');
  const [vendedor, setVendedor] = useState('');
  const [formaPago, setFormaPago] = useState('');
  const [tipo, setTipo] = useState(''); // '' | 'ventas' | 'devoluciones'
  const [importeMin, setImporteMin] = useState('');
  const [importeMax, setImporteMax] = useState('');
  const [familia, setFamilia] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortBy, setSortBy] = useState('fecha');
  const [sortDir, setSortDir] = useState('desc');

  // Data
  const [tickets, setTickets] = useState([]);
  const [lineas, setLineas] = useState([]);
  const [totalTickets, setTotalTickets] = useState(0);
  const [totalLineas, setTotalLineas] = useState(0);
  const [kpis, setKpis] = useState(null);
  const [alertas, setAlertas] = useState(null);
  const [vendedores, setVendedores] = useState([]);
  const [familias, setFamilias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [detailTicketId, setDetailTicketId] = useState(null);

  // Load filter options once
  useEffect(() => {
    epelsaVentasService?.getVendedores()?.then(setVendedores)?.catch(() => {});
    epelsaVentasService?.getFamilias()?.then(setFamilias)?.catch(() => {});
  }, []);

  const loadData = useCallback(async () => {
    if (activeTab !== 'ventas') return;
    try {
      setLoading(true); setError(null);
      if (vistaActiva === 'tickets') {
        const [result, kpiData] = await Promise.all([
          epelsaVentasService?.getTickets({ desde, hasta, search, vendedor, formaPago, tipo, importeMin, importeMax, pageSize, page, sortBy, sortDir }),
          epelsaVentasService?.getKPIs(desde, hasta),
        ]);
        setTickets(result?.tickets);
        setTotalTickets(result?.total);
        setKpis(kpiData);
      } else {
        const [result, kpiData, alertData] = await Promise.all([
          epelsaVentasService?.getLineas({ desde, hasta, search, vendedor, familia, pageSize, page, sortBy, sortDir }),
          epelsaVentasService?.getKPIs(desde, hasta),
          epelsaVentasService?.getAlerts(desde, hasta),
        ]);
        setLineas(result?.lineas);
        setTotalLineas(result?.total);
        setKpis(kpiData);
        setAlertas(alertData);
      }
    } catch (e) { setError(e?.message || 'Error al cargar datos'); }
    finally { setLoading(false); }
  }, [activeTab, vistaActiva, desde, hasta, search, vendedor, formaPago, tipo, importeMin, importeMax, familia, pageSize, page, sortBy, sortDir]);

  useEffect(() => { loadData(); }, [loadData]);

  // Mientras el agente local de Epelsa está funcionando, los tickets pueden
  // llegar después de cargar la pantalla. Refrescamos automáticamente sin
  // cambiar filtros ni página.
  useEffect(() => {
    const timer = setInterval(() => { loadData(); }, 30000);
    return () => clearInterval(timer);
  }, [loadData]);

  const applyPreset = (p) => {
    setPreset(p); const r = getPresetRange(p);
    setDesde(r?.desde); setHasta(r?.hasta); setPage(1);
  };

  const clearFilters = () => {
    setSearch(''); setVendedor(''); setFormaPago(''); setTipo('');
    setImporteMin(''); setImporteMax(''); setFamilia('');
    applyPreset('hoy'); setPage(1);
  };

  const handleSort = (col) => {
    if (sortBy === col) setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('desc'); }
    setPage(1);
  };

  const activeFilters = [
    preset && { key: 'preset', label: preset?.replace('_', ' ') },
    search && { key: 'search', label: `"${search}"` },
    vendedor && { key: 'vendedor', label: `Vendedor: ${vendedor}` },
    formaPago && { key: 'formaPago', label: formaPago },
    tipo && { key: 'tipo', label: tipo === 'ventas' ? 'Solo ventas' : 'Solo devoluciones' },
    (importeMin || importeMax) && { key: 'importe', label: `${importeMin || '0'} – ${importeMax || '∞'} €` },
    familia && { key: 'familia', label: `Familia: ${familia}` },
  ]?.filter(Boolean);

  const removeFilter = (key) => {
    if (key === 'preset') applyPreset('hoy');
    if (key === 'search') setSearch('');
    if (key === 'vendedor') setVendedor('');
    if (key === 'formaPago') setFormaPago('');
    if (key === 'tipo') setTipo('');
    if (key === 'importe') { setImporteMin(''); setImporteMax(''); }
    if (key === 'familia') setFamilia('');
    setPage(1);
  };

  const totalPages = Math.ceil((vistaActiva === 'tickets' ? totalTickets : totalLineas) / pageSize);
  const totalCount = vistaActiva === 'tickets' ? totalTickets : totalLineas;

  const PRESETS = [
    { key: 'hoy', label: 'Hoy' }, { key: 'ayer', label: 'Ayer' },
    { key: 'ultimos_7', label: 'Últimos 7 días' }, { key: 'ultimos_30', label: 'Últimos 30 días' },
    { key: 'semana', label: 'Esta semana' }, { key: 'semana_anterior', label: 'Semana anterior' },
    { key: 'mes', label: 'Este mes' }, { key: 'mes_anterior', label: 'Mes anterior' },
    { key: 'trimestre', label: 'Trimestre' }, { key: 'anio', label: 'Este año' },
  ];

  const SortIcon = ({ col }) => (
    <Icon name={sortBy === col ? (sortDir === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={11} className="inline ml-1 opacity-50" />
  );

  const kpiCards = [
    { label: 'Ventas', value: kpis ? (kpis?.ventas >= 0 ? fmtEur(kpis?.ventas) : fmtEur(kpis?.ventas)) : '—', icon: 'TrendingUp', color: ACCENT },
    { label: 'Tickets', value: kpis?.nTickets ?? '—', icon: 'Receipt', color: GOLD },
    { label: 'Ticket medio', value: kpis ? fmtEur(kpis?.ticketMedio) : '—', icon: 'ShoppingBag', color: BLUE },
    { label: 'Efectivo', value: kpis ? fmtEur(kpis?.efectivo) : '—', icon: 'Banknote', color: ACCENT },
    { label: 'Tarjeta', value: kpis ? fmtEur(kpis?.tarjeta) : '—', icon: 'CreditCard', color: PURPLE },
    { label: 'Bizum', value: kpis ? fmtEur(kpis?.bizum) : '—', icon: 'Smartphone', color: ORANGE },
  ];

  const alertasCentro = [
    alertas?.ticketsSinPago > 0 ? { nivel: 'critica', titulo: 'Tickets sin pago', detalle: `${alertas.ticketsSinPago} ticket(s) no tienen pago registrado.`, color: RED, icon: 'AlertCircle' } : null,
    alertas && Math.abs(alertas.diferenciaPagos) > 0.05 ? { nivel: 'critica', titulo: 'Descuadre de cobros', detalle: `Cobros vs ventas: ${fmtEurSigned(alertas.diferenciaPagos)}.`, color: RED, icon: 'AlertTriangle' } : null,
    alertas?.devoluciones > 0 ? { nivel: 'aviso', titulo: 'Devoluciones detectadas', detalle: `${alertas.devoluciones} devolución(es) · ${fmtEur(alertas.importeDevoluciones)}.`, color: ORANGE, icon: 'RotateCcw' } : null,
    alertas?.lineasSinFamilia > 0 ? { nivel: 'aviso', titulo: 'Artículos sin familia', detalle: `${alertas.lineasSinFamilia} línea(s) necesitan clasificación.`, color: GOLD, icon: 'Package' } : null,
    alertas?.ultimoTicket && (Date.now() - new Date(alertas.ultimoTicket).getTime()) > 45 * 60 * 1000 ? { nivel: 'aviso', titulo: 'Epelsa sin actividad reciente', detalle: `Último ticket: ${fmtTime(alertas.ultimoTicket)}.`, color: ORANGE, icon: 'Clock' } : null,
    alertas?.ticketsSinCliente > 0 ? { nivel: 'info', titulo: 'Tickets sin cliente', detalle: `${alertas.ticketsSinCliente} operación(es) sin cliente asociado.`, color: BLUE, icon: 'User' } : null,
  ].filter(Boolean);

  const alertasCriticas = alertasCentro.filter(a => a.nivel === 'critica').length;
  const alertasAviso = alertasCentro.filter(a => a.nivel === 'aviso').length;

  return (
    <div className="min-h-screen px-4 py-6 md:px-8" style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #111111 50%, #0D0D0D 100%)' }}>
      {detailTicketId && <TicketDetailModal ticketId={detailTicketId} onClose={() => setDetailTicketId(null)} />}
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${ACCENT}15`, border: `1px solid ${ACCENT}30` }}>
              <Icon name="TrendingUp" size={18} style={{ color: ACCENT }} />
            </div>
            <h1 className="font-oranienbaum font-bold text-white text-2xl md:text-3xl tracking-wide">Ventas</h1>
          </div>
          <p className="font-oranienbaum text-white/50 text-base ml-12">Control operativo de ventas · Tickets · Cobros · Artículos · Epelsa</p>
        </div>
        {activeTab === 'ventas' && (
          <button onClick={loadData} className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-oranienbaum transition-all"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', color: 'rgba(255,255,255,0.60)' }}>
            <Icon name="RefreshCw" size={13} />Actualizar
          </button>
        )}
      </div>
      {/* Tabs */}
      <div className="flex items-center gap-1 p-1 rounded-xl mb-5 overflow-x-auto" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
        {TABS?.map(tab => (
          <button key={tab?.id} onClick={() => { setActiveTab(tab?.id); setPage(1); }}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-oranienbaum whitespace-nowrap transition-all flex-shrink-0"
            style={activeTab === tab?.id
              ? { background: `${ACCENT}18`, color: ACCENT, border: `1px solid ${ACCENT}35` }
              : { color: 'rgba(255,255,255,0.40)', border: '1px solid transparent' }}>
            <Icon name={tab?.icon} size={13} />{tab?.label}
          </button>
        ))}
      </div>
      {activeTab === 'bascula' && <EpelsaBascula />}
      {activeTab === 'config-epelsa' && <EpelsaConfiguracion />}
      {activeTab === 'ventas' && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
            {kpiCards?.map((kpi, i) => (
              <div key={i} className="rounded-2xl p-4 flex flex-col gap-2" style={{ background: `linear-gradient(135deg, ${kpi?.color}0D, rgba(255,255,255,0.025))`, border: `1px solid ${kpi?.color}25`, boxShadow: `inset 0 1px 0 rgba(255,255,255,0.03)` }}>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${kpi?.color}15`, border: `1px solid ${kpi?.color}25` }}>
                  <Icon name={kpi?.icon} size={15} style={{ color: kpi?.color }} />
                </div>
                <p className="font-oranienbaum font-bold text-white text-2xl md:text-3xl">{loading ? '...' : kpi?.value}</p>
                <p className="font-oranienbaum text-white/65 text-sm leading-tight">{kpi?.label}</p>
              </div>
            ))}
          </div>

          {/* Operational intelligence */}
          <div className="grid grid-cols-1 xl:grid-cols-[1.6fr_1fr] gap-4 mb-5">
            <div className="rounded-2xl p-5" style={{ background: 'linear-gradient(135deg, rgba(52,211,153,0.08), rgba(255,255,255,0.025))', border: '1px solid rgba(52,211,153,0.18)' }}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="font-oranienbaum text-white/45 text-xs uppercase tracking-[0.18em]">Resumen ejecutivo</p>
                  <h3 className="font-oranienbaum font-bold text-white text-xl mt-1">Rendimiento del periodo</h3>
                </div>
                <span className="px-2.5 py-1 rounded-full text-xs font-oranienbaum" style={{ background: 'rgba(52,211,153,0.12)', color: ACCENT, border: '1px solid rgba(52,211,153,0.25)' }}>
                  {desde === hasta ? 'Día seleccionado' : 'Periodo seleccionado'}
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  ['Ventas', kpis ? fmtEur(kpis.ventas) : '—'],
                  ['Ticket medio', kpis ? fmtEur(kpis.ticketMedio) : '—'],
                  ['Cobro digital', kpis ? fmtEur((kpis.tarjeta || 0) + (kpis.bizum || 0)) : '—'],
                  ['Variación', alertas?.variacionPct == null ? 'Sin comparable' : `${alertas.variacionPct >= 0 ? '+' : ''}${fmt(alertas.variacionPct,1)}%`],
                ].map(([label,value], i) => (
                  <div key={i} className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}>
                    <p className="font-oranienbaum text-white/45 text-xs">{label}</p>
                    <p className="font-oranienbaum font-bold text-white text-lg mt-1">{value}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl p-5" style={{ background: alertasCriticas ? 'rgba(248,113,113,0.08)' : 'rgba(255,255,255,0.025)', border: `1px solid ${alertasCriticas ? 'rgba(248,113,113,0.25)' : 'rgba(255,255,255,0.07)'}` }}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${alertasCriticas ? RED : ACCENT}18`, border: `1px solid ${alertasCriticas ? RED : ACCENT}30` }}>
                    <Icon name="Bell" size={17} style={{ color: alertasCriticas ? RED : ACCENT }} />
                  </div>
                  <div>
                    <p className="font-oranienbaum text-white/45 text-xs uppercase tracking-wider">Centro de alertas</p>
                    <p className="font-oranienbaum font-bold text-white text-lg">{alertasCentro.length ? `${alertasCentro.length} incidencias` : 'Todo controlado'}</p>
                  </div>
                </div>
                <div className="flex gap-1.5">
                  {alertasCriticas > 0 && <span className="px-2 py-1 rounded-full text-[11px] font-oranienbaum" style={{ background: `${RED}18`, color: RED }}>{alertasCriticas} críticas</span>}
                  {alertasAviso > 0 && <span className="px-2 py-1 rounded-full text-[11px] font-oranienbaum" style={{ background: `${ORANGE}18`, color: ORANGE }}>{alertasAviso} avisos</span>}
                </div>
              </div>
              <div className="space-y-1.5 max-h-36 overflow-y-auto">
                {alertasCentro.length === 0 ? (
                  <div className="flex items-center gap-2 py-3">
                    <Icon name="CheckCircle" size={15} style={{ color: ACCENT }} />
                    <span className="font-oranienbaum text-white/60 text-sm">No hay incidencias relevantes en el periodo.</span>
                  </div>
                ) : alertasCentro.slice(0, 6).map((a, i) => (
                  <div key={i} className="flex items-start gap-2 p-2.5 rounded-xl" style={{ background: `${a.color}0D`, border: `1px solid ${a.color}18` }}>
                    <Icon name={a.icon} size={14} style={{ color: a.color, marginTop: 2 }} />
                    <div className="min-w-0">
                      <p className="font-oranienbaum font-bold text-white/85 text-sm">{a.titulo}</p>
                      <p className="font-oranienbaum text-white/45 text-xs">{a.detalle}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Vista selector */}
          <div className="flex items-center gap-2 mb-4">
            {['tickets', 'articulos']?.map((v) => (
              <button key={v} onClick={() => { setVistaActiva(v); setPage(1); setSortBy(v === 'tickets' ? 'fecha' : 'fecha_linea'); }}
                className="px-4 py-2 rounded-xl text-sm font-oranienbaum transition-all"
                style={vistaActiva === v
                  ? { background: `${GOLD}20`, border: `1px solid ${GOLD}40`, color: GOLD }
                  : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.50)' }}>
                {v === 'tickets' ? 'Tickets' : 'Artículos'}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
            {[
              ['1', 'Ventas', 'Ticket registrado', 'TrendingUp', ACCENT],
              ['2', 'Cobros', 'Pago conciliado', 'CreditCard', BLUE],
              ['3', 'Detalle', 'Líneas y cliente', 'FileText', GOLD],
              ['4', 'Caja', 'Arqueo diario', 'Wallet', PURPLE],
              ['5', 'Alertas', 'Incidencias', 'Bell', RED],
            ].map(([n, label, sub, icon, color]) => (
              <div key={n} className="rounded-xl p-3 flex items-center gap-2" style={{ background: `${color}08`, border: `1px solid ${color}18` }}>
                <span className="w-7 h-7 rounded-full flex items-center justify-center font-oranienbaum font-bold text-sm" style={{ background: `${color}18`, color }}>{n}</span>
                <div className="min-w-0">
                  <p className="font-oranienbaum font-bold text-white/80 text-sm">{label}</p>
                  <p className="font-oranienbaum text-white/35 text-xs">{sub}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div className="rounded-2xl p-5 mb-5" style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.045), rgba(255,255,255,0.018))', border: '1px solid rgba(255,255,255,0.09)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03)' }}>
            {/* Quick presets */}
            <div className="flex flex-wrap gap-2 mb-3">
              {PRESETS?.map((p) => (
                <button key={p?.key} onClick={() => applyPreset(p?.key)}
                  className="px-3 py-1.5 rounded-lg text-sm font-oranienbaum transition-all"
                  style={{ background: preset === p?.key ? `${GOLD}20` : 'rgba(255,255,255,0.04)', border: `1px solid ${preset === p?.key ? `${GOLD}40` : 'rgba(255,255,255,0.08)'}`, color: preset === p?.key ? GOLD : 'rgba(255,255,255,0.50)' }}>
                  {p?.label}
                </button>
              ))}
            </div>

            {/* Main filter row */}
            <div className="flex flex-wrap gap-3 items-center mb-3">
              <div className="relative flex-1 min-w-48">
                <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
                <input type="text" placeholder="Buscar ticket, artículo, vendedor..." value={search}
                  onChange={(e) => { setSearch(e?.target?.value); setPage(1); }}
                  className="w-full pl-9 pr-4 py-2 rounded-xl text-xs font-oranienbaum text-white/80 placeholder-white/25 outline-none"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }} />
              </div>
              <div className="flex items-center gap-2">
                <label className="font-oranienbaum text-white/40 text-xs">Desde</label>
                <input type="date" value={desde} onChange={(e) => { setPreset(''); setDesde(e?.target?.value); setPage(1); }}
                  className="px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/70 outline-none"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', colorScheme: 'dark' }} />
              </div>
              <div className="flex items-center gap-2">
                <label className="font-oranienbaum text-white/40 text-xs">Hasta</label>
                <input type="date" value={hasta} onChange={(e) => { setPreset(''); setHasta(e?.target?.value); setPage(1); }}
                  className="px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/70 outline-none"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', colorScheme: 'dark' }} />
              </div>
              <button onClick={() => setShowAdvanced((v) => !v)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-oranienbaum transition-all"
                style={{ background: showAdvanced ? `${BLUE}15` : 'rgba(255,255,255,0.04)', border: `1px solid ${showAdvanced ? `${BLUE}30` : 'rgba(255,255,255,0.08)'}`, color: showAdvanced ? BLUE : 'rgba(255,255,255,0.50)' }}>
                <Icon name="SlidersHorizontal" size={12} />Filtros avanzados
              </button>
              <button onClick={clearFilters} className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-oranienbaum transition-all"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.40)' }}>
                <Icon name="X" size={12} />Limpiar
              </button>
            </div>

            {/* Advanced filters */}
            {showAdvanced && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <div>
                  <label className="block font-oranienbaum text-white/40 text-xs mb-1">Vendedor</label>
                  <select value={vendedor} onChange={(e) => { setVendedor(e?.target?.value); setPage(1); }}
                    className="w-full px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/70 outline-none"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }}>
                    <option value="">Todos</option>
                    {vendedores?.map((v, i) => <option key={i} value={v?.vendedor_nombre || v?.vendedor_codigo}>{v?.vendedor_nombre || v?.vendedor_codigo}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block font-oranienbaum text-white/40 text-xs mb-1">Forma de pago</label>
                  <select value={formaPago} onChange={(e) => { setFormaPago(e?.target?.value); setPage(1); }}
                    className="w-full px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/70 outline-none"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }}>
                    <option value="">Todos</option>
                    <option value="Efectivo">Efectivo</option>
                    <option value="Tarjeta">Tarjeta</option>
                    <option value="Bizum">Bizum</option>
                    <option value="Otros">Otros</option>
                  </select>
                </div>
                <div>
                  <label className="block font-oranienbaum text-white/40 text-xs mb-1">Tipo</label>
                  <select value={tipo} onChange={(e) => { setTipo(e?.target?.value); setPage(1); }}
                    className="w-full px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/70 outline-none"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }}>
                    <option value="">Todos</option>
                    <option value="ventas">Solo ventas</option>
                    <option value="devoluciones">Solo devoluciones</option>
                  </select>
                </div>
                {vistaActiva === 'articulos' && (
                  <div>
                    <label className="block font-oranienbaum text-white/40 text-xs mb-1">Familia</label>
                    <select value={familia} onChange={(e) => { setFamilia(e?.target?.value); setPage(1); }}
                      className="w-full px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/70 outline-none"
                      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }}>
                      <option value="">Todas</option>
                      {familias?.map((f) => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block font-oranienbaum text-white/40 text-xs mb-1">Importe mín. (€)</label>
                  <input type="number" step="0.01" value={importeMin} onChange={(e) => { setImporteMin(e?.target?.value); setPage(1); }}
                    placeholder="0" className="w-full px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/70 outline-none"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }} />
                </div>
                <div>
                  <label className="block font-oranienbaum text-white/40 text-xs mb-1">Importe máx. (€)</label>
                  <input type="number" step="0.01" value={importeMax} onChange={(e) => { setImporteMax(e?.target?.value); setPage(1); }}
                    placeholder="∞" className="w-full px-3 py-2 rounded-xl text-xs font-oranienbaum text-white/70 outline-none"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }} />
                </div>
                {/* Quick importe ranges */}
                <div className="col-span-2">
                  <label className="block font-oranienbaum text-white/40 text-xs mb-1">Rangos rápidos</label>
                  <div className="flex flex-wrap gap-1">
                    {[['< 5 €', '', '5'], ['5–10 €', '5', '10'], ['10–20 €', '10', '20'], ['20–50 €', '20', '50'], ['> 50 €', '50', '']]?.map(([label, min, max]) => (
                      <button key={label} onClick={() => { setImporteMin(min); setImporteMax(max); setPage(1); }}
                        className="px-2 py-1 rounded-lg text-sm font-oranienbaum transition-all"
                        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.50)' }}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Active filter chips */}
            {activeFilters?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                {activeFilters?.map((f) => (
                  <span key={f?.key} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-oranienbaum"
                    style={{ background: `${BLUE}12`, border: `1px solid ${BLUE}35`, color: BLUE }}>
                    {f?.label}
                    <button onClick={() => removeFilter(f?.key)} className="hover:text-white transition-colors"><Icon name="X" size={10} /></button>
                  </span>
                ))}
                <button onClick={clearFilters} className="px-2 py-1 rounded-full text-sm font-oranienbaum transition-all"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.40)' }}>
                  Limpiar todos
                </button>
              </div>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 p-4 rounded-xl flex items-center gap-3" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.20)' }}>
              <Icon name="AlertCircle" size={16} className="text-red-400 flex-shrink-0" />
              <p className="font-oranienbaum text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Table */}
          <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="px-4 py-3 flex items-center justify-between" style={{ background: 'rgba(255,255,255,0.04)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
              <span className="font-oranienbaum text-white/50 text-xs">
                {loading ? 'Cargando...' : `${totalCount} ${vistaActiva === 'tickets' ? 'tickets' : 'líneas'} encontrados`}
              </span>
              <div className="flex items-center gap-2">
                <span className="font-oranienbaum text-white/30 text-xs">Por página:</span>
                {[25, 50, 100]?.map((n) => (
                  <button key={n} onClick={() => { setPageSize(n); setPage(1); }}
                    className="px-2 py-1 rounded-lg text-sm font-oranienbaum transition-all"
                    style={{ background: pageSize === n ? `${GOLD}20` : 'rgba(255,255,255,0.04)', border: `1px solid ${pageSize === n ? `${GOLD}40` : 'rgba(255,255,255,0.08)'}`, color: pageSize === n ? GOLD : 'rgba(255,255,255,0.40)' }}>
                    {n}
                  </button>
                ))}
              </div>
            </div>

            <div className="overflow-x-auto">
              {/* VISTA TICKETS */}
              {vistaActiva === 'tickets' && (
                <table className="w-full min-w-max">
                  <thead>
                    <tr style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      {[
                        { col: 'numero_ticket', label: 'Nº Ticket' },
                        { col: 'fecha', label: 'Fecha' },
                        { col: 'fecha', label: 'Hora' },
                        { col: 'cliente_nombre', label: 'Cliente' },
                        { col: null, label: 'Canal' },
                        { col: null, label: 'Origen' },
                        { col: 'vendedor_nombre', label: 'Vendedor' },
                        { col: null, label: 'Forma pago' },
                        { col: 'base_imponible', label: 'Base Imp.' },
                        { col: 'iva_importe', label: 'IVA' },
                        { col: 'total', label: 'Total' },
                        { col: 'estado', label: 'Estado' },
                      ]?.map(({ col, label }) => (
                        <th key={label} className="px-3 py-3 text-left whitespace-nowrap">
                          <button onClick={col ? () => handleSort(col) : undefined}
                            className={`font-oranienbaum text-white/40 text-xs uppercase tracking-wider ${col ? 'hover:text-white/60 transition-colors' : ''}`}>
                            {label}{col && <SortIcon col={col} />}
                          </button>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={12} className="px-4 py-12 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <div className="w-8 h-8 rounded-full border-2 border-white/10 border-t-[#34D399] animate-spin" />
                          <p className="font-oranienbaum text-white/40 text-sm">Cargando tickets Epelsa...</p>
                        </div>
                      </td></tr>
                    ) : tickets?.length === 0 ? (
                      <tr><td colSpan={12} className="px-4 py-16 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: `${ACCENT}08`, border: `1px solid ${ACCENT}15` }}>
                            <Icon name="TrendingUp" size={24} className="text-[#34D399]/40" />
                          </div>
                          <p className="font-oranienbaum text-white/40 text-sm">No hay tickets para los filtros seleccionados</p>
                        </div>
                      </td></tr>
                    ) : tickets?.map((t, i) => {
                      const isDevolucion = parseFloat(t?.total) < 0;
                      return (
                        <tr key={t?.id} onClick={() => setDetailTicketId(t?.id)}
                          style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', background: isDevolucion ? `${ORANGE}0D` : i % 2 === 0 ? 'rgba(255,255,255,0.018)' : 'rgba(255,255,255,0.032)', cursor: 'pointer' }}
                          className="hover:bg-white/[0.03] transition-colors">
                          <td className="px-3 py-3"><span className="font-oranienbaum text-white/95 text-base font-bold">#{t?.numero_ticket}</span></td>
                          <td className="px-3 py-3"><span className="font-oranienbaum text-white/70 text-sm">{fmtDate(t?.fecha)}</span></td>
                          <td className="px-3 py-3"><span className="font-oranienbaum text-white/70 text-sm">{fmtTime(t?.fecha)}</span></td>
                          <td className="px-3 py-3"><span className="font-oranienbaum text-white/75 text-sm">{t?.cliente_nombre || '—'}</span></td>
                          <td className="px-3 py-3">
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-oranienbaum" style={{ background: `${ACCENT}18`, color: ACCENT, border: `1px solid ${ACCENT}30` }}>Físico</span>
                          </td>
                          <td className="px-3 py-3">
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-oranienbaum" style={{ background: `${GOLD}15`, color: GOLD, border: `1px solid ${GOLD}30` }}>Epelsa</span>
                          </td>
                          <td className="px-3 py-3"><span className="font-oranienbaum text-white/80 text-sm">{t?.vendedor_nombre || t?.vendedor_codigo || '—'}</span></td>
                          <td className="px-3 py-3"><span className="font-oranienbaum text-white/60 text-xs">{t?.forma_pago ? normalizePago(t?.forma_pago, '') : '—'}</span></td>
                          <td className="px-3 py-3"><span className="font-oranienbaum text-white/70 text-sm">{fmtEur(parseFloat(t?.base_imponible))}</span></td>
                          <td className="px-3 py-3"><span className="font-oranienbaum text-white/50 text-sm">{fmtEur(parseFloat(t?.iva_importe))}</span></td>
                          <td className="px-3 py-3">
                            <span className="font-oranienbaum font-bold text-base" style={{ color: isDevolucion ? ORANGE : 'white' }}>{fmtEur(parseFloat(t?.total))}</span>
                            {isDevolucion && <span className="ml-1 font-oranienbaum text-xs px-1 py-0.5 rounded" style={{ background: `${ORANGE}15`, color: ORANGE }}>Dev.</span>}
                          </td>
                          <td className="px-3 py-3">
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-oranienbaum" style={{ background: `${ACCENT}18`, color: ACCENT, border: `1px solid ${ACCENT}30` }}>
                              {isDevolucion ? 'Devolución' : 'Cobrado'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              {/* VISTA ARTÍCULOS */}
              {vistaActiva === 'articulos' && (
                <table className="w-full min-w-max">
                  <thead>
                    <tr style={{ background: 'rgba(255,255,255,0.02)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      {[
                        { col: 'fecha_linea', label: 'Fecha' },
                        { col: 'fecha_linea', label: 'Hora' },
                        { col: null, label: 'Ticket' },
                        { col: 'articulo_codigo', label: 'Código' },
                        { col: 'descripcion', label: 'Artículo' },
                        { col: 'familia_codigo', label: 'Familia' },
                        { col: 'cantidad', label: 'Cantidad' },
                        { col: 'peso', label: 'Peso' },
                        { col: 'precio_unitario', label: 'Precio' },
                        { col: 'iva_porcentaje', label: 'IVA' },
                        { col: 'total', label: 'Total' },
                        { col: null, label: 'Vendedor' },
                      ]?.map(({ col, label }) => (
                        <th key={label} className="px-3 py-3 text-left whitespace-nowrap">
                          <button onClick={col ? () => handleSort(col) : undefined}
                            className={`font-oranienbaum text-white/40 text-xs uppercase tracking-wider ${col ? 'hover:text-white/60 transition-colors' : ''}`}>
                            {label}{col && <SortIcon col={col} />}
                          </button>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={12} className="px-4 py-12 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <div className="w-8 h-8 rounded-full border-2 border-white/10 border-t-[#34D399] animate-spin" />
                          <p className="font-oranienbaum text-white/40 text-sm">Cargando artículos...</p>
                        </div>
                      </td></tr>
                    ) : lineas?.length === 0 ? (
                      <tr><td colSpan={12} className="px-4 py-16 text-center">
                        <p className="font-oranienbaum text-white/40 text-sm">No hay artículos para los filtros seleccionados</p>
                      </td></tr>
                    ) : lineas?.map((l, i) => (
                      <tr key={l?.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}
                        className="hover:bg-white/[0.03] transition-colors">
                        <td className="px-3 py-3"><span className="font-oranienbaum text-white/60 text-xs">{fmtDate(l?.fecha_linea || l?.ticket?.fecha)}</span></td>
                        <td className="px-3 py-3"><span className="font-oranienbaum text-white/60 text-xs">{fmtTime(l?.fecha_linea || l?.ticket?.fecha)}</span></td>
                        <td className="px-3 py-3"><span className="font-oranienbaum text-white/60 text-xs">—</span></td>
                        <td className="px-3 py-3"><span className="font-oranienbaum text-white/60 text-xs">{l?.articulo_codigo || '—'}</span></td>
                        <td className="px-3 py-3"><span className="font-oranienbaum text-white/85 text-sm">{l?.descripcion || '—'}</span></td>
                        <td className="px-3 py-3"><span className="font-oranienbaum text-white/50 text-xs">{l?.familia_codigo || '—'}</span></td>
                        <td className="px-3 py-3"><span className="font-oranienbaum text-white/70 text-sm">{l?.cantidad != null ? fmt(l?.cantidad, 0) : '—'}</span></td>
                        <td className="px-3 py-3"><span className="font-oranienbaum text-white/70 text-sm">{l?.peso > 0 ? `${fmt(l?.peso, 3)} kg` : '—'}</span></td>
                        <td className="px-3 py-3"><span className="font-oranienbaum text-white/70 text-sm">{l?.precio_unitario > 0 ? fmtEur(parseFloat(l?.precio_unitario)) : '—'}</span></td>
                        <td className="px-3 py-3"><span className="font-oranienbaum text-white/50 text-sm">{l?.iva_porcentaje != null ? `${l?.iva_porcentaje}%` : '—'}</span></td>
                        <td className="px-3 py-3"><span className="font-oranienbaum font-bold text-sm" style={{ color: GOLD }}>{fmtEur(parseFloat(l?.total))}</span></td>
                        <td className="px-3 py-3"><span className="font-oranienbaum text-white/60 text-xs">{l?.ticket?.vendedor_nombre || l?.ticket?.vendedor_codigo || '—'}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            <div className="px-4 py-3 flex items-center justify-between" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              <span className="font-oranienbaum text-white/30 text-xs">
                {totalCount === 0 ? '0 – 0 de 0' : `${(page - 1) * pageSize + 1} – ${Math.min(page * pageSize, totalCount)} de ${totalCount}`} resultados
              </span>
              <div className="flex items-center gap-1">
                <button onClick={() => setPage(1)} disabled={page === 1} className="p-1.5 rounded-lg text-white/30 hover:text-white/60 transition-colors disabled:opacity-30" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
                  <Icon name="ChevronsLeft" size={14} />
                </button>
                <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="p-1.5 rounded-lg text-white/30 hover:text-white/60 transition-colors disabled:opacity-30" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
                  <Icon name="ChevronLeft" size={14} />
                </button>
                <span className="px-3 py-1 rounded-lg font-oranienbaum text-xs" style={{ background: `${ACCENT}15`, color: ACCENT }}>{page} / {totalPages || 1}</span>
                <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="p-1.5 rounded-lg text-white/30 hover:text-white/60 transition-colors disabled:opacity-30" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
                  <Icon name="ChevronRight" size={14} />
                </button>
                <button onClick={() => setPage(totalPages)} disabled={page >= totalPages} className="p-1.5 rounded-lg text-white/30 hover:text-white/60 transition-colors disabled:opacity-30" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
                  <Icon name="ChevronsRight" size={14} />
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TiendaVentas;
